// Pixel helpers for the places' drawing (docs/186 EA7): packed colours, fast seeded hashes for per-pixel
// choices, connected runs of pixels, the slow seeded field the ruins drift by. All seeded through the
// board's salt, so every client draws the same picture (docs/15 §3.2).
import type { Piece } from '../types';
import type { Canvas } from './canvas';
import type { Ctx } from './context';

const t8 = new Uint8ClampedArray(4), t32 = new Int32Array(t8.buffer);
/** One RGBA pixel as the platform's 32-bit word (the layout of a `Int32Array` over a `Canvas`). */
export function pack(r: number, g: number, b: number, a = 255): number { t8[0] = r; t8[1] = g; t8[2] = b; t8[3] = a; return t32[0]!; }
export const ALPHA = pack(0, 0, 0, 255);
export const opaque = (v: number): boolean => (v & ALPHA) !== 0;
export function unpack(v: number): [number, number, number, number] { t32[0] = v; return [t8[0]!, t8[1]!, t8[2]!, t8[3]!]; }

/** A canvas's pixels as words (a fresh Canvas's buffer starts at 0). */
export const words = (c: Canvas): Int32Array => new Int32Array(c.px.buffer, c.px.byteOffset, c.w * c.h);

const pieceWords = new WeakMap<Piece, Int32Array>();
export function pieceWordsOf(p: Piece): Int32Array {
  let w = pieceWords.get(p);
  if (!w) { w = new Int32Array(p.px.slice().buffer); pieceWords.set(p, w); }
  return w;
}
/** A piece's pixel (x, y) as a word. */
export const at = (p: Piece, x: number, y: number): number => pieceWordsOf(p)[y * p.w + x]!;

/** `ctx.roll(purpose, x, y)` without building a string per call: the same hash (`cellHash`), its prefix done once. */
export function hasher(ctx: Ctx, purpose: string): (x: number, y: number) => number {
  let h0 = 0x811c9dc5;
  const s = `${ctx.salt}|${purpose}`;
  for (let i = 0; i < s.length; i++) { h0 ^= s.charCodeAt(i); h0 = Math.imul(h0, 0x01000193); }
  return (x, y) => {
    let h = h0;
    h ^= x + 0x9e3779b9; h = Math.imul(h, 0x01000193);
    h ^= y + 0x85ebca6b; h = Math.imul(h, 0x01000193);
    h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
    return h >>> 0;
  };
}

/** A slow seeded field in 0-100 over board pixels (`ruin_field`): where moss drifts, with no square in it. */
export function fieldOf(ctx: Ctx, purpose = 'mf'): (gx: number, gy: number, cell?: number) => number {
  const f = hasher(ctx, purpose);
  // the corners of the last cell asked, as a scan asks the same cell many times running
  let la = NaN, lb = NaN, lc = NaN, p00 = 0, p10 = 0, p01 = 0, p11 = 0;
  return (gx, gy, cell = 64) => {
    const u = gx / cell, v = gy / cell, a = Math.floor(u), b = Math.floor(v), t = u - a, s = v - b;
    if (a !== la || b !== lb || cell !== lc) { la = a; lb = b; lc = cell; p00 = f(a, b) % 100; p10 = f(a + 1, b) % 100; p01 = f(a, b + 1) % 100; p11 = f(a + 1, b + 1) % 100; }
    return (p00 * (1 - t) + p10 * t) * (1 - s) + (p01 * (1 - t) + p11 * t) * s;
  };
}

/** Grows as needed: the pixels of one connected run. */
class Cells {
  buf = new Int32Array(4096);
  n = 0;
  push(i: number): void { if (this.n === this.buf.length) { const b = new Int32Array(this.buf.length * 2); b.set(this.buf); this.buf = b; } this.buf[this.n++] = i; }
}

/** The middle of a run (integer mean, as the tool's `//`). */
export function middle(cells: Int32Array, n: number, W: number): [number, number] {
  let sx = 0, sy = 0;
  for (let k = 0; k < n; k++) { const i = cells[k]!; sx += i % W; sy += Math.floor(i / W); }
  return [Math.floor(sx / n), Math.floor(sy / n)];
}

/** A fast test for a few colours (a set's joints): no hashing per pixel. */
export function oneOf(colours: Iterable<number>): (v: number) => boolean {
  const a = [...colours];
  if (!a.length) return () => false;
  if (a.length <= 4) {
    const [p, q = p, r = p, s = p] = a as [number, number?, number?, number?];
    return (v) => v === p || v === q || v === r || v === s;
  }
  const set = new Set(a);
  return (v) => set.has(v);
}

/** Every 4-connected run of pixels whose `mask` is `want`, in scan order: `visit(cells, n)` with the run's pixel indices in `cells[0..n)` (the first is where the scan met it). */
export function maskRuns(mask: Uint8Array, W: number, H: number, want: number, visit: (cells: Int32Array, n: number) => void): void {
  const seen = new Uint8Array(W * H), c = new Cells(), N = W * H;
  for (let s = 0; s < N; s++) {
    if (seen[s] || mask[s] !== want) continue;
    c.n = 0; c.push(s); seen[s] = 1;
    for (let k = 0; k < c.n; k++) {
      const i = c.buf[k]!, x = i % W;
      if (x + 1 < W && !seen[i + 1] && mask[i + 1] === want) { seen[i + 1] = 1; c.push(i + 1); }
      if (x > 0 && !seen[i - 1] && mask[i - 1] === want) { seen[i - 1] = 1; c.push(i - 1); }
      if (i + W < N && !seen[i + W] && mask[i + W] === want) { seen[i + W] = 1; c.push(i + W); }
      if (i >= W && !seen[i - W] && mask[i - W] === want) { seen[i - W] = 1; c.push(i - W); }
    }
    visit(c.buf, c.n);
  }
}
